import { FunctionComponent, useState, useEffect, useRef } from 'react';
import {
    Box, Header, Table, SpaceBetween, Link,
    Select, SelectProps, DateRangePicker, DateRangePickerProps,
    Pagination, CollectionPreferences, PropertyFilter, PropertyFilterProps,
} from '@amzn/awsui-components-react';
import { getString, getPropertyFilterString } from 'common/ui-string-labels/ui-strings-utils';
import { buildColumnDefinitions, defaultPagingOptions, buildVisibleContentOptions } from 'common/utils/table-utils';
import { convertTokensToPropertyFilter } from 'common/utils/property-filter-helper';
import { describeError } from 'common/utils/error-utils';
import { ColumnSetting } from 'common/utils/config-models';
import { DATE_RANGE_RELATIVE_OPTIONS, formatRelativeRange } from 'common/utils/date-range-filter-utils';
import { usePreferences } from 'hooks/usePreferences';
import tableConfig from './reseller-reports.table-config.json';
import { ResellerReport, ResellerReportHistoryTableProps, VENDOR_OPTIONS, STATUS_OPTIONS, FILTERING_PROPERTIES } from './interfaces';
import { ResellerReportService } from 'services/reseller-report-service';

const PREFERENCES_KEY = 'reseller-report-history-preferences';
const VISIBLE_CONTENT_OPTIONS = buildVisibleContentOptions(tableConfig.columns as ColumnSetting[]);


/** Converts a DateRangePicker value into dateFrom/dateTo query params. */
export function buildDateRangeParams(dateRange: any): { dateFrom: string; dateTo: string } | null {
    if (!dateRange) return null;
    if (dateRange.type === 'absolute') {
        return { dateFrom: dateRange.startDate, dateTo: dateRange.endDate };
    }
    if (dateRange.type === 'relative') {
        const now = new Date();
        const start = new Date(now);
        switch (dateRange.unit) {
            case 'day': start.setDate(start.getDate() - dateRange.amount); break;
            case 'week': start.setDate(start.getDate() - dateRange.amount * 7); break;
            case 'month': start.setMonth(start.getMonth() - dateRange.amount); break;
            case 'year': start.setFullYear(start.getFullYear() - dateRange.amount); break;
            default: start.setDate(start.getDate() - dateRange.amount); break;
        }
        return { dateFrom: start.toISOString().split('T')[0], dateTo: now.toISOString().split('T')[0] };
    }
    return null;
}

export const ResellerReportHistoryTable: FunctionComponent<ResellerReportHistoryTableProps> = ({ useFlashBarItemsActions }) => {
    const [items, setItems] = useState<ResellerReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>([undefined]);

    // Filters
    const [vendorFilter, setVendorFilter] = useState<SelectProps.Option>(VENDOR_OPTIONS[0] as SelectProps.Option);
    const [statusFilter, setStatusFilter] = useState<SelectProps.Option>(STATUS_OPTIONS[0] as SelectProps.Option);
    const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);
    const [filterQuery, setFilterQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: 'and' });

    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey: PREFERENCES_KEY,
        defaultPaging: 10,
        columnsSettings: tableConfig.columns as ColumnSetting[],
        refreshData: () => {},
    });

    const fetchIdRef = useRef(0);

    const doFetch = (page: number) => {
        const id = ++fetchIdRef.current;
        setLoading(true);
        const token = tokenHistory[page - 1];

        const params: any = {
            vendor: vendorFilter.value || undefined,
            status: statusFilter.value || undefined,
            amazonPo: convertTokensToPropertyFilter(filterQuery.tokens).amazonPo?.[0] || undefined,
            limit: currentPreferences.pageSize!,
            nextToken: token,
        };

        // Apply date range params
        const dateParams = buildDateRangeParams(dateRange);
        /* istanbul ignore next -- dateRange state requires DateRangePicker interaction */
        if (dateParams) {
            params.dateFrom = dateParams.dateFrom;
            params.dateTo = dateParams.dateTo;
        }

        ResellerReportService.instance.getReports(params).then(data => {
            if (fetchIdRef.current === id) {
                setItems(data.items);
                setNextToken(data.nextToken);
                if (data.nextToken) {
                    setTokenHistory(prev => {
                        const updated = [...prev];
                        updated[page] = data.nextToken!;
                        return updated;
                    });
                }
                setLoading(false);
            }
        }).catch((error) => {
            if (fetchIdRef.current === id) {
                setLoading(false);
                useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'resellerReportHistory.flashbar.error'));
            }
        });
    };

    useEffect(() => {
        // Reset cursor state when filters change (currentPage will be 1)
        if (currentPage === 1) {
            setTokenHistory([undefined]);
            setNextToken(null);
        }
        doFetch(currentPage);
    }, [vendorFilter.value, statusFilter.value, dateRange, currentPage, filterQuery]);

    // Reset to page 1 when pageSize changes to avoid stale cursors
    const prevPageSize = useRef(currentPreferences.pageSize);
    useEffect(() => {
        if (prevPageSize.current !== currentPreferences.pageSize) {
            prevPageSize.current = currentPreferences.pageSize;
            setTokenHistory([undefined]);
            setNextToken(null);
            if (currentPage === 1) {
                doFetch(1);
            } else {
                setCurrentPage(1);
            }
        }
    }, [currentPreferences.pageSize]);

    const onDownload = (report: ResellerReport) => {
        ResellerReportService.instance.getDownloadUrl(report.taxonomyId).then((url) => {
            window.open(url, '_blank');
        }).catch((error) => {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'resellerReportHistory.flashbar.error'));
        });
    };

    const baseColumns = buildColumnDefinitions(tableConfig.columns as ColumnSetting[], currentPreferences.visibleContent);
    const columns = [
        ...baseColumns,
        {
            id: 'download',
            header: getString('resellerReportHistory.columns.download'),
            cell: (item: ResellerReport) => (
                <Link onFollow={() => onDownload(item)}>{getString('resellerReportHistory.actions.download')}</Link>
            ),
        },
    ];

    return (
        <Table<ResellerReport>
            data-testid="reseller-report-history-table"
            variant="full-page"
            loading={loading}
            header={<Header variant="h1">{getString('resellerReportHistory.title')}</Header>}
            columnDefinitions={columns}
            items={items}
            stripedRows={currentPreferences.stripedRows}
            resizableColumns
            pagination={<Pagination currentPageIndex={currentPage} pagesCount={nextToken ? currentPage + 1 : currentPage} openEnd={nextToken !== null} onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)} />}
            preferences={<CollectionPreferences title={getString('common.preferences.title')} confirmLabel={getString('common.preferences.confirmLabel')} cancelLabel={getString('common.preferences.cancelLabel')} preferences={currentPreferences} onConfirm={onPreferencesConfirmHandler} pageSizePreference={{ title: getString('common.preferences.pageSize.title'), options: defaultPagingOptions }} stripedRowsPreference={{ label: getString('common.preferences.stripedRows.label'), description: getString('common.preferences.stripedRows.description') }} visibleContentPreference={{ title: getString('common.preferences.visibleContentPreference.title'), options: [{ label: getString('common.preferences.visibleContentPreference.columns'), options: VISIBLE_CONTENT_OPTIONS }] }} />}
            filter={
                <SpaceBetween direction="horizontal" size="s">
                    <div data-testid="reseller-report-history-property-filter" style={{ width: "500px" }}>
                        <PropertyFilter
                            query={filterQuery}
                            onChange={({ detail }) => { setFilterQuery(detail); setCurrentPage(1); }}
                            filteringProperties={FILTERING_PROPERTIES}
                            filteringPlaceholder="Search reports..."
                            i18nStrings={getPropertyFilterString()}
                            expandToViewport
                        />
                    </div>
                    <Select selectedOption={vendorFilter} onChange={({ detail }) => { setVendorFilter(detail.selectedOption); setCurrentPage(1); }} options={VENDOR_OPTIONS} ariaLabel={getString('resellerReportHistory.filter.vendorAriaLabel')} selectedAriaLabel="Selected" />
                    <Select selectedOption={statusFilter} onChange={({ detail }) => { setStatusFilter(detail.selectedOption); setCurrentPage(1); }} options={STATUS_OPTIONS} ariaLabel={getString('resellerReportHistory.filter.statusAriaLabel')} selectedAriaLabel="Selected" />
                    <DateRangePicker
                        value={dateRange}
                        onChange={({ detail }) => { setDateRange(detail.value); setCurrentPage(1); }}
                        placeholder={getString('resellerReportHistory.filter.dateRangePlaceholder')}
                        relativeOptions={DATE_RANGE_RELATIVE_OPTIONS}
                        isValidRange={() => ({ valid: true })}
                        dateOnly
                        i18nStrings={{
                            todayAriaLabel: getString('manageOrders.dateRangePicker.todayAriaLabel'),
                            nextMonthAriaLabel: getString('manageOrders.dateRangePicker.nextMonthAriaLabel'),
                            previousMonthAriaLabel: getString('manageOrders.dateRangePicker.previousMonthAriaLabel'),
                            customRelativeRangeDurationLabel: getString('manageOrders.dateRangePicker.customRelativeRangeDurationLabel'),
                            customRelativeRangeDurationPlaceholder: getString('manageOrders.dateRangePicker.customRelativeRangeDurationPlaceholder'),
                            customRelativeRangeOptionLabel: getString('manageOrders.dateRangePicker.customRelativeRangeOptionLabel'),
                            customRelativeRangeOptionDescription: getString('manageOrders.dateRangePicker.customRelativeRangeOptionDescription'),
                            customRelativeRangeUnitLabel: getString('manageOrders.dateRangePicker.customRelativeRangeUnitLabel'),
                            formatRelativeRange,
                            relativeModeTitle: getString('manageOrders.dateRangePicker.relativeModeTitle'),
                            absoluteModeTitle: getString('manageOrders.dateRangePicker.absoluteModeTitle'),
                            relativeRangeSelectionHeading: getString('manageOrders.dateRangePicker.relativeRangeSelectionHeading'),
                            startDateLabel: getString('manageOrders.dateRangePicker.startDateLabel'),
                            endDateLabel: getString('manageOrders.dateRangePicker.endDateLabel'),
                            startTimeLabel: getString('manageOrders.dateRangePicker.startTimeLabel'),
                            endTimeLabel: getString('manageOrders.dateRangePicker.endTimeLabel'),
                            clearButtonLabel: getString('manageOrders.dateRangePicker.clearButtonLabel'),
                            cancelButtonLabel: getString('manageOrders.dateRangePicker.cancelButtonLabel'),
                            applyButtonLabel: getString('manageOrders.dateRangePicker.applyButtonLabel'),
                        }}
                    />
                </SpaceBetween>
            }
            empty={<Box textAlign="center"><b>{getString('resellerReportHistory.table.emptyTitle')}</b></Box>}
            stickyHeader
            wrapLines
        />
    );
};
